import React from 'react';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Button, Space } from 'antd';
import {
  DisputeReviewFragmentType,
  PerformDisputeAction,
} from '@pay-bill/graphql';
import {
  DisputeCommentInput,
  DisputeFilesInput,
  DisputeFilesSection,
  DrawerFooter,
  DrawerTitle,
} from '@pay-bill/components';
import { useDisputeDeletedFiles, useDisputeRefetchs } from '@pay-bill/hooks';
import { catchErrorWith, rejectSimpleErrors } from '@pay-bill/services';
import { Banner, Form, Col, Row, Divider } from '@common/components';
import { useCurrentEntity } from '@identity/hooks';
import { Store } from 'antd/lib/form/interface';

export function DisputeApprovalDrawerBody({
  dispute,
  goBack,
}: {
  dispute: DisputeReviewFragmentType;
  goBack: () => void;
}) {
  const currentEntity = useCurrentEntity();
  const isOrganization = currentEntity.entityType === 'Organization';
  const [removedFiles, onFileDelete, maxFilesSlots] = useDisputeDeletedFiles(
    dispute.files.organization
  );

  const [performAction, { loading: loadingPerform }] =
    useMutation(PerformDisputeAction);

  const [form] = Form.useForm();
  const refetchQueries = useDisputeRefetchs();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = ({ comment, files }: Store) => {
    if (loadingPerform) {
      return;
    }

    performAction({
      variables: {
        disputeId: dispute.id,
        input: {
          action: isOrganization ? 'approve' : 'accept',
          comment: comment,
          addedFiles: files || [],
          removedFiles: removedFiles.map((file) => file.id),
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage(
          'success',
          `Dispute ${isOrganization ? 'approved' : 'accepted'}`
        );
        refetchQueries();
        goBack();
      })
      .catch(catchErrorWith('Failed to approve dispute. Please try again.'));
  };

  return (
    <>
      <DrawerTitle>{isOrganization ? 'Approve' : 'Accept'} Dispute</DrawerTitle>
      <div
        className="dispute-approval-drawer-body"
        style={{ padding: '0px 24px 0px 24px', flex: 1, overflow: 'auto' }}
      >
        <Form
          onFinish={onFinish}
          form={form}
          layout="vertical"
          initialValues={{
            comment: '',
            files: [],
          }}
        >
          <Col>
            <Banner style={{ marginTop: '12px' }} type="info" closable>
              Adding a note and attaching a file are optional. Click
              &apos;Approve&apos; to skip.
            </Banner>
          </Col>
          <DisputeCommentInput
            containerStyle={{ marginTop: '12px' }}
            placeholder="Add a comment"
            required={false}
          />
          <Row className="dispute-upload-row">
            <Col span={24}>
              <DisputeFilesInput maxFilesSlots={maxFilesSlots} />
            </Col>
          </Row>
          <Divider gutter={16} />
          <DisputeFilesSection
            dispute={dispute}
            canDeleteFiles="Organization"
            onDelete={onFileDelete}
            hiddenFiles={removedFiles}
          />
        </Form>
      </div>
      <DrawerFooter>
        <Space>
          <Col span={9}>
            <Button
              className="dispute-go-back-button"
              type="default"
              size="large"
              onClick={goBack}
            >
              Back
            </Button>
          </Col>
          <Col span={9}>
            <Button
              type="primary"
              size="large"
              onClick={form.submit}
              loading={loadingPerform}
              id="dispute-approval-button"
            >
              {isOrganization ? 'Approve' : 'Accept'}
            </Button>
          </Col>
        </Space>
      </DrawerFooter>
      <style jsx>
        {`
          .dispute-approval-drawer-body :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
        `}
      </style>
    </>
  );
}
